export interface BreathworkWorkout {
  id?: string | null;
  breathworkId: string;
  startedAt: Date;
  endedAt: Date;
  /** Total session duration in seconds */
  totalDurationSec: number;
  rounds: number;
  breathsOnFirstRound?: number | null;
  incrementBetweenRounds?: boolean | null;
  /** Inhale duration in seconds */
  inhaleDurationSec?: number | null;
  /** Full-lungs retention duration in seconds */
  fullLungsRetentionDurationSec?: number | null;
  /** Exhale duration in seconds */
  exhaleDurationSec?: number | null;
  /** Empty-lungs retention duration in seconds */
  emptyLungsRetentionDurationSec?: number | null;
  timezoneOffsetFromUtc?: number | null;
  priorFeelingsIds?: string[] | null;
  postFeelingsIds?: string[] | null;
  /** Journey context — populated when this workout is part of a journey step */
  journeyStepSessionId?: string | null;
  journeyId?: string | null;
  journeyName?: string | null;
  journeyStepTitle?: string | null;
  journeyStepIndex?: number | null;
}

/**
 * Returns a simplified ratio string such as "4:7:8" or null when no
 * meaningful ratio can be calculated. Ported from BreathworkWorkout.ratio getter.
 */
export function getBreathworkWorkoutRatio(workout: BreathworkWorkout): string | null {
  const { inhaleDurationSec: inhale, exhaleDurationSec: exhale } = workout;
  if (inhale == null || exhale == null || inhale < 1 || exhale < 1) return null;

  const parts: number[] = [inhale];
  if (workout.fullLungsRetentionDurationSec != null) parts.push(workout.fullLungsRetentionDurationSec);
  parts.push(exhale);
  if (workout.emptyLungsRetentionDurationSec != null) parts.push(workout.emptyLungsRetentionDurationSec);

  const nonZeroParts = parts.filter((v) => v > 0);
  const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));
  const gcdValue = nonZeroParts.reduce(gcd);
  const simplified = nonZeroParts.map((v) => String(v / gcdValue)).join(':');

  return simplified;
}

/**
 * Parses a BreathworkWorkout from a plain-JSON record.
 * startedAt / endedAt must already be Date objects (Firestore Timestamps
 * are converted upstream in firestoreParsers before reaching this function).
 */
export function breathworkWorkoutFromJson(json: Record<string, unknown>): BreathworkWorkout {
  return {
    id: (json['id'] as string | null) ?? null,
    breathworkId: json['breathworkId'] as string,
    startedAt: json['startedAt'] as Date,
    endedAt: json['endedAt'] as Date,
    totalDurationSec: json['totalDuration'] as number,
    rounds: json['rounds'] as number,
    breathsOnFirstRound: (json['breathsOnFirstRound'] as number | null) ?? null,
    incrementBetweenRounds: (json['incrementBetweenRounds'] as boolean | null) ?? null,
    inhaleDurationSec: (json['inhaleDuration'] as number | null) ?? null,
    fullLungsRetentionDurationSec: (json['fullLungsRetentionDuration'] as number | null) ?? null,
    exhaleDurationSec: (json['exhaleDuration'] as number | null) ?? null,
    emptyLungsRetentionDurationSec: (json['emptyLungsRetentionDuration'] as number | null) ?? null,
    timezoneOffsetFromUtc: (json['timezoneOffsetFromUtc'] as number | null) ?? null,
    priorFeelingsIds: json['priorFeelingsIds'] != null ? (json['priorFeelingsIds'] as string[]) : null,
    postFeelingsIds: json['postFeelingsIds'] != null ? (json['postFeelingsIds'] as string[]) : null,
    journeyStepSessionId: (json['journeyStepSessionId'] as string | null) ?? null,
    journeyId: (json['journeyId'] as string | null) ?? null,
    journeyName: (json['journeyName'] as string | null) ?? null,
    journeyStepTitle: (json['journeyStepTitle'] as string | null) ?? null,
    journeyStepIndex: (json['journeyStepIndex'] as number | null) ?? null,
  };
}
